import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { bbox as bboxStrategy } from 'ol/loadingstrategy'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import type OlMap from 'ol/Map'
import type Feature from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'
import type { Coordinate } from 'ol/coordinate'
import type { Extent } from 'ol/extent'
import { ApiError, parseErrorResponse } from '../api/client'
import { PROXY_BASE } from '../config'
import { layerInfoOf, type WmsTileLayer } from './layers'

/** Hard cap per GetFeature request (P1.1) — keeps huge real layers loadable. */
export const WFS_FEATURE_LIMIT = 1000

export interface VectorModeController {
  /** Swap the layer between WMS tiles and its WFS vector twin. Returns true when now in vector mode. */
  toggle(wms: WmsTileLayer): boolean
  isVector(wms: WmsTileLayer): boolean
  /**
   * Features of this layer's twin at a map coordinate — the click path for a
   * layer drawn as shapes, whose WMS layer is hidden and would answer nothing.
   *
   * Deliberately geometric, not pixel-based: opening the Details panel resizes
   * the map between the click and this call, so a coordinate→pixel conversion
   * lands up to 180px off (measured). Geometry does not move when the layout
   * does. `tolerance` is in map units — the caller converts pixels via the
   * view resolution, for shapes too small or too thin to hit exactly.
   *
   * Returns empty unless the layer is *actually* in shapes mode. That guard is
   * the contract, not an optimisation: since T3 a layer with Attributes open
   * also owns a twin, rendered with an empty style. Probing it would report a
   * layer the user cannot see.
   */
  hitsAt(wms: WmsTileLayer, coordinate: Coordinate, tolerance?: number): Feature<Geometry>[]
  /**
   * Load the layer's WFS features *without changing what the map draws* (T3).
   * OpenLayers only runs a vector source's loader while the layer is being
   * rendered, so "attributes without vector rendering" is achieved by keeping
   * the twin rendered with an empty style — data flows, nothing is painted.
   * This is what makes the Attributes toggle independent of the Vector toggle.
   */
  setAttributes(wms: WmsTileLayer, on: boolean): void
  /** Fired whenever a twin finishes loading features, so a table can refresh. */
  onFeatures(cb: (wms: WmsTileLayer) => void): void
  /** Visibility entry point for the panel checkbox — applies to whichever representation is active. */
  setVisible(wms: WmsTileLayer, visible: boolean): void
  /** The panel-row visibility (what the checkbox shows), independent of the WMS/vector swap. */
  rowVisible(wms: WmsTileLayer): boolean
  featuresOf(wms: WmsTileLayer): Feature<Geometry>[]
}

/** Renders nothing — see `setAttributes` in the controller. */
const EMPTY_STYLE: Style[] = []

const VECTOR_STYLE = new Style({
  stroke: new Stroke({ color: '#b35c00', width: 1.5 }),
  fill: new Fill({ color: 'rgba(217, 119, 6, 0.15)' }),
  image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#b35c00' }) }),
})

/**
 * WFS vector mode (PRD P1.1). Each WMS layer gets a lazily-built vector twin:
 * bbox-strategy GetFeature through the proxy with credentials (bbox sent in
 * EPSG:3857, GeoJSON returned in EPSG:4326), client-side styling, and a feature
 * cap. A 403 on the WFS route silently reverts the layer to WMS — defense in
 * depth; the proxy enforces the plan regardless of what the UI offers.
 */
export function createVectorMode(
  map: OlMap,
  onEnforcement: (err: ApiError) => void,
): VectorModeController {
  const twins = new Map<WmsTileLayer, VectorLayer<VectorSource>>()
  const inVector = new Map<WmsTileLayer, boolean>()
  const attributes = new Map<WmsTileLayer, boolean>()
  const desired = new Map<WmsTileLayer, boolean>()
  const featureListeners: Array<(wms: WmsTileLayer) => void> = []

  function apply(wms: WmsTileLayer): void {
    const visible = desired.get(wms) ?? wms.getVisible()
    const vector = inVector.get(wms) === true
    const attrs = attributes.get(wms) === true
    const twin = twins.get(wms)
    wms.setVisible(visible && !vector)
    // Render the twin in either case, but paint it only in vector mode: an
    // attributes-only twin exists purely to make its loader run.
    twin?.setVisible(visible && (vector || attrs))
    twin?.setStyle(vector ? VECTOR_STYLE : EMPTY_STYLE)
  }

  /** Ensure the twin exists and the row's checkbox state is captured. */
  function ensureTwin(wms: WmsTileLayer): void {
    if (!desired.has(wms)) desired.set(wms, wms.getVisible())
    if (!twins.has(wms)) twins.set(wms, buildTwin(wms))
  }

  function revertToWms(wms: WmsTileLayer): void {
    inVector.set(wms, false)
    // Attributes ride on the same twin: leaving them on would keep the layer
    // rendered, re-running the loader against a route that just refused us.
    attributes.set(wms, false)
    apply(wms)
  }

  function handleWfsError(wms: WmsTileLayer, err: ApiError): void {
    if (err.kind === 'auth' || err.kind === 'seat' || err.kind === 'subscription' || err.kind === 'unavailable') {
      onEnforcement(err)
      return
    }
    // plan/layer/service/unknown: never invent UI rules — just fall back to WMS.
    console.warn('WFS rejected for', layerInfoOf(wms).name, '— reverting to WMS:', err.message)
    revertToWms(wms)
  }

  function buildTwin(wms: WmsTileLayer): VectorLayer<VectorSource> {
    const info = layerInfoOf(wms)
    const source: VectorSource = new VectorSource({
      strategy: bboxStrategy,
      loader: (extent, _resolution, projection, success, failure) => {
        const params = new URLSearchParams({
          service: 'WFS',
          version: '2.0.0',
          request: 'GetFeature',
          typenames: info.name,
          outputFormat: 'application/json',
          srsName: 'EPSG:4326',
          count: String(WFS_FEATURE_LIMIT),
          bbox: `${extent.join(',')},EPSG:3857`,
        })
        fetch(`${PROXY_BASE}/geoserver/${info.workspace}/wfs?${params.toString()}`, {
          credentials: 'include',
        })
          .then(async (res) => {
            if (!res.ok) throw await parseErrorResponse(res)
            const json: unknown = await res.json()
            const features = new GeoJSON().readFeatures(json, {
              dataProjection: 'EPSG:4326',
              featureProjection: projection,
            }) as Feature<Geometry>[]
            source.addFeatures(features)
            success?.(features)
            // The table renders a snapshot; tell it more rows just arrived.
            for (const cb of featureListeners) cb(wms)
          })
          .catch((err: unknown) => {
            failure?.()
            handleWfsError(
              wms,
              err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)),
            )
          })
      },
    })
    const layer = new VectorLayer({ source, style: VECTOR_STYLE, zIndex: 15, visible: false })
    map.addLayer(layer)
    return layer
  }

  return {
    toggle(wms) {
      // Capture the panel-checkbox state before the WMS layer gets hidden,
      // so flipping back restores what the user had (incl. deep-link state).
      ensureTwin(wms)
      inVector.set(wms, !(inVector.get(wms) === true))
      apply(wms)
      return inVector.get(wms) === true
    },
    isVector: (wms) => inVector.get(wms) === true,
    hitsAt(wms, coordinate, tolerance = 0) {
      // Explicit, and deliberately not relying on the twin's empty style: the
      // style is an implementation detail of `apply()`, this is a contract.
      if (inVector.get(wms) !== true) return []
      const source = twins.get(wms)?.getSource()
      if (!source) return []
      const exact = source.getFeaturesAtCoordinate(coordinate) as Feature<Geometry>[]
      if (exact.length > 0 || tolerance <= 0) return exact
      // Nothing straddles the point: widen to a small box, for shapes too thin
      // or too small to land on exactly (points, narrow strips).
      const [x, y] = coordinate
      const box: Extent = [x - tolerance, y - tolerance, x + tolerance, y + tolerance]
      return (source.getFeaturesInExtent(box) as Feature<Geometry>[]).filter((feature) =>
        feature.getGeometry()?.intersectsExtent(box),
      )
    },
    setAttributes(wms, on) {
      ensureTwin(wms)
      attributes.set(wms, on)
      apply(wms)
    },
    onFeatures(cb) {
      featureListeners.push(cb)
    },
    setVisible(wms, visible) {
      desired.set(wms, visible)
      apply(wms)
    },
    rowVisible: (wms) => desired.get(wms) ?? wms.getVisible(),
    featuresOf: (wms) => twins.get(wms)?.getSource()?.getFeatures() ?? [],
  }
}
